import type React from "react"
import { useEffect, useMemo, useRef, useState } from "react"

// Paging is not a change of working set — the rows picked on page 1 are still
// the rows picked on returning to it — and re-ordering only reshuffles the same
// result. Neither should discard a selection.
const PAGINATION_KEYS = new Set([
  "page",
  "page_size",
  "pageSize",
  "offset",
  "limit",
  "ordering"
])

/**
 * Builds a stable token from a filter object. Feed it to `useRowSelection` so a
 * selection is dropped when the filters genuinely change which rows come back,
 * but survives paging, sorting, and a re-render handing over an equal object.
 */
export const selectionResetToken = (filters: unknown): string => {
  if (!filters || typeof filters !== "object") {
    return JSON.stringify(filters ?? null)
  }
  const entries = Object.entries(filters as Record<string, unknown>)
    .filter(
      ([key, value]) =>
        !PAGINATION_KEYS.has(key) &&
        value != null &&
        value !== "" &&
        !(Array.isArray(value) && value.length === 0)
    )
    .sort(([a], [b]) => a.localeCompare(b))
  return JSON.stringify(entries)
}

/**
 * Row selection for a table that only needs to show what is selected — no bulk
 * actions, so the keys stay here rather than being lifted to the page.
 *
 * Pair with `selectionMode="on-demand"` on EntityTable for the design's three
 * states: no checkboxes at rest, the hovered row's checkbox on hover, and every
 * checkbox plus the header's select-all once something is picked.
 *
 * Tables that do need the keys (the leads bulk bar) keep owning their own state
 * and pass `rowSelection` directly instead.
 */
export function useRowSelection(resetToken?: unknown): {
  selectedRowKeys: React.Key[]
  preserveSelectedRowKeys: true
  onChange: (keys: React.Key[]) => void
} {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])

  const firstToken = useRef(true)
  useEffect(() => {
    // The token's opening value is not a change, so it must not wipe a
    // selection made before the first re-render.
    if (firstToken.current) {
      firstToken.current = false
      return
    }
    setSelectedRowKeys((keys) => (keys.length ? [] : keys))
  }, [resetToken])

  return useMemo(
    () => ({
      selectedRowKeys,
      // Rows picked on one page stay picked once they unmount, so paging away
      // and back shows them still ticked.
      preserveSelectedRowKeys: true as const,
      onChange: (keys: React.Key[]) => setSelectedRowKeys(keys)
    }),
    [selectedRowKeys]
  )
}
